/**
 * Shared cart line pricing for simple (non-deal) items.
 *
 * Model:
 * - item.modifiers[].quantity is PER UNIT of the parent item (not pre-scaled by item qty)
 * - item.modifiers[].price is the catalog/display unit price
 * - item.modifiers[].chargePrice (optional) is what the customer pays per unit after freeSelection
 *   at add-to-cart time — preferred for totals so checkout does not re-interpret API groups
 * - line total = (base + unitModifiers) * item.quantity
 */

function readFreeSelection(group) {
    const raw = group?.freeSelection ?? group?.FreeSelection ?? 0;
    const n = parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function readModifierType(group) {
    if (group?.type === "single" || group?.type === "multiple") return group.type;
    const maxSel = group?.maximumSelection ?? group?.MaximumSelection;
    if (maxSel === 1) return "single";
    return "multiple";
}

function collectGroupOptionNames(group) {
    const names = new Set();
    const add = (value) => {
        if (!value) return;
        names.add(String(value));
        names.add(String(value).toLowerCase());
    };
    (group?.options || []).forEach((opt) => {
        add(opt?.name || opt?.modifierName || opt?.MenuItem_Name);
    });
    (group?.modifiers || group?.Modifiers || []).forEach((mod) => {
        add(mod?.name || mod?.modifierName || mod?.MenuItem_Name);
    });
    return names;
}

function unitChargeForModifier(mod) {
    if (mod?.chargePrice !== undefined && mod?.chargePrice !== null && mod?.chargePrice !== "") {
        return parseFloat(mod.chargePrice) || 0;
    }
    return parseFloat(mod?.price || 0) || 0;
}

/**
 * Unit modifier total for one parent item (before × item.quantity).
 * Prefers stamped chargePrice; otherwise applies freeSelection like the modifier popup.
 */
export function getCartItemUnitModifiersTotal(item) {
    const modifiers = Array.isArray(item?.modifiers) ? item.modifiers : [];
    if (modifiers.length === 0) return 0;

    const hasStampedCharge = modifiers.some(
        (mod) => mod?.chargePrice !== undefined && mod?.chargePrice !== null && mod?.chargePrice !== ""
    );
    if (hasStampedCharge) {
        return modifiers.reduce((sum, mod) => {
            const qty = parseFloat(mod?.quantity || 1) || 1;
            return sum + unitChargeForModifier(mod) * qty;
        }, 0);
    }

    const groups = Array.isArray(item?.modifierGroups) ? item.modifierGroups : [];
    if (groups.length === 0) {
        return modifiers.reduce((sum, mod) => {
            const qty = parseFloat(mod?.quantity || 1) || 1;
            return sum + (parseFloat(mod?.price || 0) || 0) * qty;
        }, 0);
    }

    let modifiersTotal = 0;
    const processedModifiers = new Set();

    groups.forEach((group) => {
        const groupId = group?.id || group?.modifierGroupId || group?.ModifierGroupID;
        if (groupId === "variants" || (groupId && String(groupId).includes("variant"))) return;

        const freeSelection = readFreeSelection(group);
        const modifierType = readModifierType(group);
        const groupOptionNames = collectGroupOptionNames(group);

        const groupModifiers = [];
        modifiers.forEach((mod) => {
            const modName = mod?.name;
            if (
                modName &&
                !processedModifiers.has(modName) &&
                (groupOptionNames.has(modName) || groupOptionNames.has(String(modName).toLowerCase()))
            ) {
                groupModifiers.push(mod);
                processedModifiers.add(modName);
            }
        });

        if (modifierType === "single") {
            if (freeSelection < 1 && groupModifiers.length > 0) {
                const mod = groupModifiers[0];
                const qty = parseFloat(mod?.quantity || 1) || 1;
                modifiersTotal += (parseFloat(mod?.price || 0) || 0) * qty;
            }
        } else {
            groupModifiers.forEach((mod, index) => {
                if (index >= freeSelection) {
                    const qty = parseFloat(mod?.quantity || 1) || 1;
                    modifiersTotal += (parseFloat(mod?.price || 0) || 0) * qty;
                }
            });
        }
    });

    // Unmatched modifiers (not found in any group) — charge full catalog price
    const matchedOptionNames = new Set();
    groups.forEach((group) => {
        collectGroupOptionNames(group).forEach((n) => matchedOptionNames.add(n));
    });

    modifiers.forEach((mod) => {
        const modName = mod?.name;
        if (
            modName &&
            !processedModifiers.has(modName) &&
            !matchedOptionNames.has(modName) &&
            !matchedOptionNames.has(String(modName).toLowerCase())
        ) {
            const qty = parseFloat(mod?.quantity || 1) || 1;
            modifiersTotal += (parseFloat(mod?.price || 0) || 0) * qty;
        }
    });

    // Safety net: group freeSelection matching sometimes zeros every priced topping
    // (PascalCase FreeSelection / option-name mismatches) while chips still show £.
    const catalogSum = modifiers.reduce((sum, mod) => {
        const qty = parseFloat(mod?.quantity || 1) || 1;
        return sum + (parseFloat(mod?.price || 0) || 0) * qty;
    }, 0);
    if (modifiersTotal <= 0 && catalogSum > 0) {
        return catalogSum;
    }

    return modifiersTotal;
}

export function getCartItemUnitBasePrice(item) {
    return (
        parseFloat(
            item?.discountedPrice ??
                item?.basePrice ??
                item?.originalPrice ??
                item?.price ??
                item?.totalPrice ??
                0
        ) || 0
    );
}

/** Full line total for a simple cart row (excludes deal/combo special pricing). */
export function getSimpleCartItemLineTotal(item) {
    const itemQuantity = parseFloat(item?.quantity || 1) || 1;
    const unitBase = getCartItemUnitBasePrice(item);
    const unitMods = getCartItemUnitModifiersTotal(item);
    return (unitBase + unitMods) * itemQuantity;
}
